import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
  DesignChange,
  DesignChangeReviewStatus,
  UpdateDesignChangeReviewRequest,
} from '../../../types/drbfm';
import { DesignChangeCard } from './workbench/DesignChangeCard';
import { WorkbenchSection } from './workbench/WorkbenchSection';

interface DesignChangeWorkbenchProps {
  changes: DesignChange[];
  isLoading: boolean;
  onReviewChange: (
    designChangeId: string,
    request: UpdateDesignChangeReviewRequest,
  ) => void | boolean | Promise<void | boolean>;
  onConfirmComparability?: (designChangeId: string, rationale: string) => void | Promise<void>;
}

const generationStatuses: DesignChangeReviewStatus[] = ['accepted', 'modified'];

const DesignChangeWorkbench: React.FC<DesignChangeWorkbenchProps> = ({
  changes,
  isLoading,
  onReviewChange,
  onConfirmComparability,
}) => {
  const { t } = useTranslation();

  const statusLabels: Record<string, string> = {
    ai_candidate: t('drbfm.workflow.workbenches.statusAiCandidate', 'AI候補'),
    needs_info: t('drbfm.workflow.workbenches.statusNeedsInfo', '要確認'),
    accepted: t('drbfm.workflow.workbenches.statusAccepted', '採用'),
    modified: t('drbfm.workflow.workbenches.statusModified', '修正済み'),
    rejected: t('drbfm.workflow.workbenches.statusRejected', '却下'),
    deferred: t('drbfm.workflow.workbenches.statusDeferred', '保留'),
    superseded: t('drbfm.workflow.workbenches.statusSuperseded', '旧版'),
  };

  const targetCount = changes.filter((change) => (
    generationStatuses.includes(change.reviewStatus || 'ai_candidate' as DesignChangeReviewStatus)
  )).length;

  return (
    <WorkbenchSection
      title={t('drbfm.workflow.workbenches.designChangeReviewTitle', '変更点レビュー')}
      titleAs="h2"
      description={t('drbfm.workflow.workbenches.failureModeTargetCount', '故障モード生成対象 {{count}}件', { count: targetCount })}
      emptyMessage={t('drbfm.workflow.workbenches.designChangeEmpty', '変更点候補はまだありません。')}
      isEmpty={changes.length === 0}
    >
      {changes.map((change, index) => (
        <DesignChangeCard
          key={change.id || index}
          change={change}
          index={index}
          statusLabel={
            statusLabels[change.reviewStatus || 'ai_candidate'] || (change.reviewStatus as string)
          }
          isGenerationTarget={
            generationStatuses.includes(change.reviewStatus as DesignChangeReviewStatus)
          }
          isLoading={isLoading}
          onReviewChange={onReviewChange}
          onConfirmComparability={onConfirmComparability}
        />
      ))}
    </WorkbenchSection>
  );
};

export default DesignChangeWorkbench;
